import { useEffect, useRef, useState } from "react";
import { Check, Palette } from "lucide-react";
import { THEMES, applyTheme, loadTheme } from "../../lib/theme";

import { useGame } from "../../state/GameProvider";
import type { Preferences } from "../../lib/preferences";

export function SettingsMenu() {
  const { preferences, setPreferences } = useGame();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(loadTheme());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    ref.current?.querySelector<HTMLElement>(".settings-pop button")?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        ref.current?.querySelector<HTMLElement>(".tb-btn")?.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    function onDoc(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function pick(id: string) {
    setTheme(id);
    applyTheme(id);
  }

  return (
    <div className="settings" ref={ref}>
      <button
        className={`tb-btn${open ? " on" : ""}`}
        onClick={() => setOpen((o) => !o)}
        title="閱讀與主題設定"
        aria-label="閱讀與主題設定"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Palette size={15} />
      </button>
      {open && (
        <div className="settings-pop" role="dialog" aria-label="閱讀與主題設定">
          <div className="sp-title">色系</div>
          <div className="sp-themes">
            {THEMES.map((t) => (
              <button
                key={t.id}
                className={`sp-swatch${theme === t.id ? " on" : ""}`}
                onClick={() => pick(t.id)}
                aria-pressed={theme === t.id}
              >
                <span className="sw-chip" style={{ background: t.swatch }} />
                <span>{t.label}</span>
                {theme === t.id && <Check size={13} />}
              </button>
            ))}
          </div>
          <label className="reading-setting">
            <input type="checkbox" checked={preferences.autoAdvance}
              onChange={event => setPreferences({ ...preferences, autoAdvance: event.target.checked })} />
            <span>答對後自動繼續（可暫停）</span>
          </label>
          <label className="reading-setting">
            <span>正文樣式</span>
            <select aria-label="正文樣式" value={preferences.readingFont} onChange={event => setPreferences({ ...preferences, readingFont: event.target.value as Preferences["readingFont"] })}>
              <option value="clear">清晰正文</option><option value="classic">古典字體</option>
            </select>
          </label>
          <label className="reading-setting">
            <span>閱讀字級</span>
            <select aria-label="閱讀字級" value={preferences.textScale} onChange={event => setPreferences({ ...preferences, textScale: Number(event.target.value) as Preferences["textScale"] })}>
              {[1, 1.25, 1.5, 2].map(scale => <option key={scale} value={scale}>{`${scale * 100}%`}</option>)}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}
